import { MessageSquare, Zap, Camera, FileText, Globe, Video, Music, Database, Bot, Workflow } from "lucide-react";
import type { WorkflowData } from './types.ts';

export const workflowCategories = [
  "All Workflows",
  "Text Processing",
  "Image Generation",
  "Video Production",
  "Data Analysis",
  "Automation",
  "Chat & AI",
];

export const featuredWorkflows: WorkflowData[] = [
  {
    id: "1",
    title: "AI Content Writer",
    description: "Generate high-quality blog posts, articles, and marketing copy with AI assistance. Includes SEO optimization and multiple tone options.",
    category: "Text Processing",
    tags: ["GPT", "Content", "SEO", "Marketing"],
    author: {
      name: "Sarah Chen",
      role: "Content Strategist",
      verified: true,
    },
    icon: FileText,
    complexity: "Beginner",
    nodes: 8,
    uses: 15420,
    preview: "/placeholder.svg",
    // comments: [],
    rating: 4.8,
  },
  {
    id: "2",
    title: "Social Media Automation",
    description: "Automatically schedule and post content across multiple social platforms. Includes hashtag optimization and engagement tracking.",
    category: "Automation",
    tags: ["Social Media", "Scheduling", "Engagement"],
    author: {
      name: "Mike Rodriguez",
      role: "Marketing Automation",
      verified: true,
    },
    icon: Globe,
    complexity: "Intermediate",
    nodes: 15,
    uses: 8943,
    preview: "/placeholder.svg",
    // comments: [],
    rating: 4.6,
  },
  {
    id: "3",
    title: "Image Style Transfer",
    description: "Transform images using AI style transfer techniques. Apply artistic styles from famous painters or custom reference images.",
    category: "Image Generation",
    tags: ["AI Art", "Style Transfer", "Creative"],
    author: {
      name: "Alex Kim",
      role: "AI Artist",
      verified: false,
    },
    icon: Camera,
    complexity: "Advanced",
    nodes: 22,
    uses: 5672,
    preview: "/placeholder.svg",
  },
  {
    id: "4",
    title: "Customer Support Chatbot",
    description: "Intelligent chatbot that handles customer inquiries, provides support, and escalates complex issues to human agents.",
    category: "Chat & AI",
    tags: ["Chatbot", "Support", "NLP", "Customer Service"],
    author: {
      name: "Emma Thompson",
      role: "Product Manager",
      verified: true,
    },
    icon: MessageSquare,
    complexity: "Intermediate",
    nodes: 18,
    uses: 12890,
    preview: "/placeholder.svg",
    // comments: [],
    rating: 4.7,
  },
  {
    id: "5",
    title: "Data Analytics Dashboard",
    description: "Real-time analytics dashboard that processes data from multiple sources and generates insightful visualizations and reports.",
    category: "Data Analysis",
    tags: ["Analytics", "Dashboard", "Visualization", "BI"],
    author: {
      name: "David Park",
      role: "Data Scientist",
      verified: true,
    },
    icon: Database,
    complexity: "Advanced",
    nodes: 31,
    uses: 3428,
    preview: "/placeholder.svg",
    // comments: [],
    rating: 4.2,
  },
  {
    id: "6",
    title: "Video Transcription & Summary",
    description: "Automatically transcribe videos and generate concise summaries with key points and timestamps.",
    category: "Video Production",
    tags: ["Transcription", "Summary", "Video", "AI"],
    author: {
      name: "Lisa Wang",
      role: "Content Creator",
      verified: false,
    },
    icon: Video,
    complexity: "Intermediate",
    nodes: 12,
    uses: 7256,
    preview: "/placeholder.svg",
    // comments: [],
    rating: 4.5,
  },
  {
    id: "7",
    title: "Smart Email Assistant",
    description: "AI-powered email management that sorts, prioritizes, and suggests responses to your emails automatically.",
    category: "Automation",
    tags: ["Email", "AI Assistant", "Productivity"],
    author: {
      name: "James Wilson",
      role: "Productivity Expert",
      verified: true,
    },
    icon: Bot,
    complexity: "Beginner",
    nodes: 9,
    uses: 18723,
    preview: "/placeholder.svg",
    // comments: [],
    rating: 4.9,
  },
  {
    id: "8",
    title: "Music Composition AI",
    description: "Generate original music compositions in various styles and genres using advanced AI models.",
    category: "Text Processing",
    tags: ["Music", "AI Generation", "Creative", "Audio"],
    author: {
      name: "Tom Martinez",
      role: "Music Producer",
      verified: false,
    },
    icon: Music,
    complexity: "Advanced",
    nodes: 26,
    uses: 4891,
    preview: "/placeholder.svg",
    // comments: [],
    rating: 4.1,
  },
  {
    id: "9",
    title: "Lead Generation Pipeline",
    description: "Automated lead generation and qualification system that finds and scores potential customers from multiple sources.",
    category: "Automation",
    tags: ["Lead Generation", "CRM", "Sales", "Marketing"],
    author: {
      name: "Rachel Green",
      role: "Sales Operations",
      verified: true,
    },
    icon: Workflow,
    complexity: "Intermediate",
    nodes: 20,
    uses: 6134,
    preview: "/placeholder.svg",
    // comments: [],
    rating: 4.4,
  },
];

export function getComplexityColor(complexity: string) {
  switch (complexity) {
    case "Beginner":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
    case "Intermediate":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
    case "Advanced":
      return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
  }
}